import axios from 'axios';
import { push as historyPush } from 'js/history';
import { fetchCart } from './CartActions';
import tokenManager from '../services/tokenManager';
import authService from '../services/authService';
import { socketDisconnect } from './SocketActions';
import { getApiDocRedirectTarget, redirectToApiDoc } from '../helpers/apiDocRedirect';

export const HANDLE_LOGIN = 'HANDLE_LOGIN';
export const HANDLE_LOGOUT = 'HANDLE_LOGOUT';
export const OAUTH_ERROR = 'OAUTH_ERROR';
export const LOGIN_REDIRECT = 'LOGIN_REDIRECT';
export const FORCE_LOGOUT = 'FORCE_LOGOUT';
export const TOKEN_REFRESH_SUCCESS = 'TOKEN_REFRESH_SUCCESS';

export const SET_LOGIN_FORWARD_URL = 'SET_LOGIN_FORWARD_URL';
export const SET_ANONYMOUS_USER_DATA = 'SET_ANONYMOUS_USER_DATA';

export function submitLogin({ username, password }, urlPrefix = '/') {
  return dispatch => {
    return dispatch({
      type: HANDLE_LOGIN,
      payload: authService
        .login(username, password, urlPrefix)
        .then(response => {
          const { accessToken, user } = response;

          // Store access token in memory (refresh token is in httpOnly cookie)
          tokenManager.setTokens(accessToken);

          const apiDocReturnPath = getApiDocRedirectTarget();
          if (apiDocReturnPath) {
            redirectToApiDoc(apiDocReturnPath);
            return { data: user };
          }

          dispatch(loginRedirect());

          // Return user data in expected format for reducer
          return { data: user };
        })
        .catch(error => {
          // Clear any partial tokens on login failure
          tokenManager.clearTokens();
          throw error;
        }),
    });
  };
}

export function loginRedirect() {
  return (dispatch, getState) => {
    const {
      intl,
      login: { forwardUrl },
    } = getState();
    let url = forwardUrl || intl.urlPrefix + 'account';
    if(window.location.hostname.includes('creacheck-wahlkampf')){
      url = forwardUrl || intl.urlPrefix + 'pages/cdu_btw_startseite';
    }
    setTimeout(() => historyPush(url), 0);

    return dispatch({
      type: LOGIN_REDIRECT,
      payload: { url },
    });
  };
}

export function submitLoginWithoutRedirect({ username, password }, urlPrefix = '/') {
  return dispatch => {
    return dispatch({
    type: HANDLE_LOGIN,
      payload: authService
        .login(username, password, urlPrefix)
        .then(response => {
          const { accessToken, user } = response;

          // Store access token in memory (refresh token is in httpOnly cookie)
          tokenManager.setTokens(accessToken);

          // Return user data in expected format for reducer
          return { data: user };
        })
        .catch(error => {
          // Clear any partial tokens on login failure
          tokenManager.clearTokens();
          throw error;
        }),
    });
  };
}

export function submitLogout(urlPrefix = '/') {
  return dispatch => {
    dispatch(socketDisconnect());
    const redirectUrl = `${urlPrefix}login`;

    const cleanUpAfterLogout = () => {
      // Clear access token from memory
      tokenManager.clearTokens();
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.removeItem('impersonationAccessToken');
        window.sessionStorage.removeItem('impersonationUser');
      }
      if (typeof window !== 'undefined') {
        window.isImpersonating = false;
      }

      historyPush(redirectUrl);

      // Return empty data to indicate logged out state
      return { data: '' };
    };

    return dispatch({
      type: HANDLE_LOGOUT,
      payload: authService
        .logout(urlPrefix)
        .then(cleanUpAfterLogout)
        .catch(error => {
          // Clear access token even if logout API call fails
          console.error('Logout error:', error);
          return cleanUpAfterLogout();
        }),
    });
  };
}

/**
 * Force logout (called when token refresh fails or on 401/403 errors)
 */
export function forceLogout(reason = 'unknown') {
  return dispatch => {
    // Clear tokens immediately
    tokenManager.clearTokens();
    dispatch(socketDisconnect());

    dispatch({
      type: FORCE_LOGOUT,
      payload: { reason },
    });

    // Redirect to login page after a short delay
    setTimeout(() => {
      historyPush('/login');
    }, 100);
  };
}

/**
 * Refresh access token
 */
export function refreshAccessToken() {
  return dispatch => {
    const refreshToken = tokenManager.getRefreshToken();

    if (!refreshToken) {
      return dispatch(forceLogout('no_refresh_token'));
    }

    return authService
      .refreshToken(refreshToken)
      .then(response => {
        const { accessToken, refreshToken: newRefreshToken } = response;

        // Update tokens
        tokenManager.setTokens(accessToken, newRefreshToken || refreshToken);

        dispatch({
          type: TOKEN_REFRESH_SUCCESS,
          payload: { accessToken },
        });

        return response;
      })
      .catch(error => {
        console.error('Token refresh failed:', error);
        dispatch(forceLogout('token_refresh_failed'));
        throw error;
      });
  };
}

/**
 * Get guest token for anonymous users
 */
export function getGuestToken(email, name) {
  return dispatch => {
    return authService
      .getGuestToken(email, name)
      .then(response => {
        const { accessToken, user } = response;

        // Store access token (no refresh token for guests)
        tokenManager.setTokens(accessToken, null);

        dispatch({
          type: HANDLE_LOGIN,
          payload: Promise.resolve({ data: user }),
        });

        return response;
      });
  };
}

export function oauthError(service, error) {
  return {
    type: OAUTH_ERROR,
    payload: { service, error },
  };
}

export function setLoginForwardUrl(url) {
  return {
    type: SET_LOGIN_FORWARD_URL,
    payload: url,
  };
}

export function setAnonymousUserData(data) {
  return {
    type: SET_ANONYMOUS_USER_DATA,
    payload: data,
  };
}
